/** Contesto di autenticazione: sessione utente, scelta e cambio del tenant. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { api, ApiError, CHIAVE_TOKEN, getToken, setToken, tempiToken, tokenChiosco } from '../api/client'
import type { Principal, TenantOption } from '../api/types'
import { t } from '../i18n/lingua'
import { DOPO_CAMBIO_TENANT, sezioneDi } from './sezioneTenant'

/**
 * Scelta del tenant rimasta in sospeso dopo l'accesso.
 *
 * Esiste perché l'utente appartiene a più tenant e il token di accesso è legato
 * a uno solo: finché non è stato scelto non c'è nulla da emettere.
 */
export interface PendingChoice {
  token: string
  tenants: TenantOption[]
}

interface AuthState {
  principal: Principal | null
  loading: boolean
  /** La verifica iniziale sta impiegando più del solito (Control Plane in riavvio?). */
  verificaLenta: boolean
  /** Valorizzata quando le credenziali sono corrette ma manca la scelta del tenant. */
  pendingChoice: PendingChoice | null
  /** Valorizzata quando serve il codice dell'app di autenticazione. */
  pendingMfa: { token: string } | null
  completeMfa: (code: string) => Promise<void>
  login: (email: string, password: string) => Promise<void>
  /** Chiude l'accesso avviato su un provider di identità. */
  completeOidc: (state: string, code: string) => Promise<void>
  chooseTenant: (tenantId: string) => Promise<void>
  switchTenant: (tenantId: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [principal, setPrincipal] = useState<Principal | null>(null)
  const [loading, setLoading] = useState(true)
  const [verificaLenta, setVerificaLenta] = useState(false)
  const [pendingChoice, setPendingChoice] = useState<PendingChoice | null>(null)
  const [pendingMfa, setPendingMfa] = useState<{ token: string } | null>(null)

  // Al primo caricamento verifica se il token in sessione è ancora valido.
  //
  // Con timeout e **riprova**, per due difetti visti in campo:
  //  - una `me()` che non risponde (Control Plane in riavvio, migrazioni
  //    all'avvio, proxy che tiene la connessione) lasciava la console su
  //    «Verifica sessione…» a tempo indefinito, con l'aria di essere bloccata.
  //    Ora dopo il timeout lo dice e riprova, e si riprende da sé appena il
  //    Control Plane risponde.
  //  - su un errore di **rete** (non un 401) si cancellava il token valido e si
  //    finiva al login per un singolo blip. Ora il token si toglie **solo** sul
  //    401 vero (sessione non più valida); sulla rete si riprova e basta.
  useEffect(() => {
    // A wall monitor opened through its link has no session to verify.
    if (tokenChiosco() || !getToken()) {
      setLoading(false)
      return
    }
    let annullato = false
    async function verifica() {
      while (!annullato) {
        try {
          const p = await Promise.race([
            api.me(),
            new Promise<never>((_, rej) =>
              setTimeout(() => rej(new ApiError(0, 'timeout', 'timeout')), 10_000)),
          ])
          if (!annullato) {
            setPrincipal(p)
            setVerificaLenta(false)
            setLoading(false)
          }
          return
        } catch (e) {
          // Sessione davvero non valida: al login, senza riprovare.
          if (e instanceof ApiError && e.status === 401) {
            if (!annullato) {
              setToken(null)
              setLoading(false)
            }
            return
          }
          // Rete o timeout: il Control Plane può essere in riavvio. Si tiene il
          // token, si dice che è in corso e si riprova fra poco.
          if (!annullato) setVerificaLenta(true)
          await new Promise((r) => setTimeout(r, 2000))
        }
      }
    }
    void verifica()
    return () => {
      annullato = true
    }
  }, [])

  // Più schede, una sessione: se un'altra scheda esce, si esce anche qui; se
  // un'altra entra (o cambia tenant), qui si riprende il profilo.
  useEffect(() => {
    const suCambio = (e: StorageEvent) => {
      if (e.key !== CHIAVE_TOKEN) return
      if (!e.newValue) {
        setPrincipal(null)
        setPendingChoice(null)
        return
      }
      api.me().then(setPrincipal).catch(() => undefined)
    }
    window.addEventListener('storage', suCambio)
    return () => window.removeEventListener('storage', suCambio)
  }, [])

  // La sessione scade per **inattività**, con la durata decisa dal tenant: se
  // nell'ultimo tratto qualcuno ha usato la console (in questa scheda o in
  // un'altra: il token è condiviso) e il token è oltre metà della sua vita, lo
  // si rinnova. Senza attività lo si lascia scadere, e il prossimo 401 riporta
  // al login.
  useEffect(() => {
    if (!principal) return
    let ultimaAttivita = Date.now()
    const segna = () => { ultimaAttivita = Date.now() }
    const eventi = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const
    eventi.forEach((ev) => window.addEventListener(ev, segna, { passive: true }))
    const controlla = () => {
      const tempi = tempiToken()
      if (!tempi) return
      const adesso = Date.now()
      const restante = tempi.scade - adesso
      if (restante <= 0) return
      const attivo = adesso - ultimaAttivita < tempi.durata / 2
      if (attivo && restante < tempi.durata / 2) {
        api.refreshSession().then((r) => { if (r.access_token) setToken(r.access_token) }).catch(() => undefined)
      }
    }
    const intervallo = window.setInterval(controlla, 60_000)
    return () => {
      window.clearInterval(intervallo)
      eventi.forEach((ev) => window.removeEventListener(ev, segna))
    }
  }, [principal])

  const login = useCallback(async (email: string, password: string) => {
    const resp = await api.login(email, password)

    if (resp.mfa_required && resp.mfa_token) {
      setPendingMfa({ token: resp.mfa_token })
      return
    }
    if (resp.tenant_choice_required && resp.tenant_choice_token) {
      setPendingChoice({ token: resp.tenant_choice_token, tenants: resp.tenants })
      return
    }
    if (!resp.access_token) throw new Error(t('risposta di accesso senza token'))

    setToken(resp.access_token)
    setPrincipal(await api.me())
  }, [])

  /**
   * Chiude un accesso con il provider di identità.
   *
   * Passa dalla stessa strada di `login` — token, scelta del tenant, profilo —
   * perché dopo l'autenticazione **non c'è differenza**: chi entra con l'IdP e
   * chi entra con la password hanno gli stessi ruoli sugli stessi tenant. Due
   * strade separate divergerebbero al primo cambiamento, e la seconda si
   * accorgerebbe dei problemi della prima con mesi di ritardo.
   */
  const completeOidc = useCallback(async (state: string, code: string) => {
    const resp = await api.oidcCallback(state, code)

    if (resp.tenant_choice_required && resp.tenant_choice_token) {
      setPendingChoice({ token: resp.tenant_choice_token, tenants: resp.tenants })
      return
    }
    if (!resp.access_token) throw new Error(t('risposta di accesso senza token'))

    setToken(resp.access_token)
    setPrincipal(await api.me())
  }, [])

  /**
   * Il secondo passaggio: il codice dell'app di autenticazione.
   *
   * Dopo il codice si riprende la STESSA strada del login — token o scelta del
   * tenant — perché a valle del secondo fattore non c'è differenza.
   */
  const completeMfa = useCallback(
    async (code: string) => {
      if (!pendingMfa) return
      const resp = await api.mfaVerify(pendingMfa.token, code)
      setPendingMfa(null)
      if (resp.tenant_choice_required && resp.tenant_choice_token) {
        setPendingChoice({ token: resp.tenant_choice_token, tenants: resp.tenants })
        return
      }
      if (!resp.access_token) throw new Error(t('risposta di accesso senza token'))
      setToken(resp.access_token)
      setPrincipal(await api.me())
    },
    [pendingMfa],
  )

  const chooseTenant = useCallback(
    async (tenantId: string) => {
      if (!pendingChoice) return
      const resp = await api.selectTenant(pendingChoice.token, tenantId)
      if (!resp.access_token) throw new Error(t('risposta di accesso senza token'))

      setToken(resp.access_token)
      setPendingChoice(null)
      setPrincipal(await api.me())
    },
    [pendingChoice],
  )

  const switchTenant = useCallback(async (tenantId: string) => {
    const resp = await api.switchTenant(tenantId)
    if (!resp.access_token) throw new Error(t('risposta di cambio tenant senza token'))
    setToken(resp.access_token)

    // Ricarica invece di aggiornare il solo principal: ogni schermata ha in
    // memoria i dati del tenant precedente, e lasciarli lì mostrerebbe gli
    // CI di un cliente sotto il nome di un altro.
    // The reload lands on the same section (no object, no parameters: they
    // belong to the previous tenant); the layout sends to home when the new
    // tenant does not have it (sezioneTenant.ts).
    try {
      sessionStorage.setItem(DOPO_CAMBIO_TENANT, '1')
    } catch {
      // without session storage the section is kept, the check is skipped
    }
    window.location.assign(sezioneDi(window.location.pathname))
  }, [])

  const logout = useCallback(() => {
    setToken(null)
    setPrincipal(null)
    setPendingChoice(null)
  }, [])

  const value = useMemo(
    () => ({
      principal, loading, verificaLenta, pendingChoice, pendingMfa, completeMfa, login,
      completeOidc, chooseTenant, switchTenant, logout,
    }),
    [
      principal, loading, verificaLenta, pendingChoice, pendingMfa, completeMfa, login,
      completeOidc, chooseTenant, switchTenant, logout,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve essere usato dentro AuthProvider')
  return ctx
}
